import { useRef, KeyboardEvent } from 'react';
import { useEnvironment } from 'context/EnvironmentContext/EnvironmentContext';
import { Environment } from 'types';
import { styled, v2Colors } from 'theme';
import {
  environmentSurface,
  environmentDot,
  environmentLabel,
} from 'theme/environmentColours';

const Segment = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '$2',
  backgroundColor: v2Colors.plane,
  border: `1px solid ${v2Colors.line}`,
  borderRadius: '11px',
  p: '$1',
  mb: '$5',
});

const Option = styled('button', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '$2',
  border: '1px solid transparent',
  borderRadius: '$md',
  px: '$2',
  py: '$2',
  backgroundColor: 'transparent',
  color: v2Colors.muted,
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'background-color 120ms, color 120ms, border-color 120ms',
  '&:focus-visible': { boxShadow: '$focus' },
});

const Dot = styled('span', {
  width: '7px',
  height: '7px',
  borderRadius: '$full',
  backgroundColor: 'currentcolor',
  opacity: 0.5,
});

const ENVIRONMENTS: readonly Environment[] = ['live', 'pre-live'];

/**
 * A radio group, not two buttons: the selection has to reach a screen reader
 * through `aria-checked`, not through the background colour.
 */
function EnvironmentSegmentV2() {
  const { environment, setEnvironment } = useEnvironment();
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // `useLocalStorage` does not validate, so a corrupted `__environment__` that
  // still parses as JSON gives -1 here. Unclamped, ArrowLeft would index -2 and
  // call `setEnvironment(undefined)`, stripping both radios' accessible names.
  const selectedIndex = Math.max(0, ENVIRONMENTS.indexOf(environment));

  const selectAndFocus = (index: number) => {
    setEnvironment(ENVIRONMENTS[index]);
    optionRefs.current[index]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const lastIndex = ENVIRONMENTS.length - 1;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        selectAndFocus(selectedIndex === lastIndex ? 0 : selectedIndex + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        selectAndFocus(selectedIndex === 0 ? lastIndex : selectedIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        selectAndFocus(0);
        break;
      case 'End':
        event.preventDefault();
        selectAndFocus(lastIndex);
        break;
      default:
        break;
    }
  };

  return (
    <Segment
      role="radiogroup"
      aria-label="Environment"
      onKeyDown={handleKeyDown}
    >
      {ENVIRONMENTS.map((value, index) => {
        const isSelected = environment === value;
        return (
          <Option
            key={value}
            ref={(el: HTMLButtonElement | null) => {
              optionRefs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={isSelected}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => setEnvironment(value)}
            css={
              isSelected
                ? { ...environmentSurface[value], fontWeight: '$bold' }
                : undefined
            }
          >
            <Dot
              aria-hidden="true"
              css={
                isSelected
                  ? { backgroundColor: environmentDot[value], opacity: 1 }
                  : undefined
              }
            />
            {environmentLabel[value]}
          </Option>
        );
      })}
    </Segment>
  );
}

export default EnvironmentSegmentV2;
